import { useEffect, useState, useCallback } from 'react';
import { gql } from '@apollo/client';
import { useToast } from './useToast';
import { FlattenedUser } from '../types/user';
import { useSelector } from 'react-redux';
import { UserType } from '../types/generated';
import {
	useClientOnboardingModal_GetClientUserWhereQuery,
	useClientOnboardingModal_UpdateSelfMutation,
} from '#types';
import { ClientOnboardingModalProps } from '../blocks/ClientOnboardingModal';
import { useBlockingRouter } from './useBlockingRouter';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const clientOnboardingModal_GetClientUserWhere = gql`
	query clientOnboardingModal_GetClientUserWhere(
		$where: ClientUserWhereUniqueInput!
	) {
		getClientUserWhere(where: $where) {
			id
			user {
				id
				seenOnboardingModalAt
			}
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const clientOnboardingModal_UpdateSelf = gql`
	mutation clientOnboardingModal_UpdateSelf($data: UserUpdateSelfInput!) {
		updateSelf(data: $data) {
			id
			seenOnboardingModalAt
		}
	}
`;

export const useClientOnboardingModal = (): ClientOnboardingModalProps & {
	setOpen: React.Dispatch<React.SetStateAction<boolean | undefined>>;
	isClientUser: boolean;
} => {
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const { showError } = useToast();
	const [ open, setOpen ] = useState<boolean | undefined>();
	const router = useBlockingRouter();

	const clientUser =
		user?.isLoggedIn && user.userType === UserType.ClientUser
			? user
			: undefined;

	const [ updateSelf ] = useClientOnboardingModal_UpdateSelfMutation();

	const apiResponse = useClientOnboardingModal_GetClientUserWhereQuery( {
		variables: { where: { id: clientUser?.id || '' } },
		skip: !clientUser?.id,
	} );

	// on mount, if we've just onboarded and are on the dashboard, show the modal
	useEffect( () => {
		const fetchAndUpdate = async () => {
			if ( apiResponse.error ) {
				showError( apiResponse.error );
			} else if (
				apiResponse.data?.getClientUserWhere?.user &&
				router.pathname === '/'
			) {
				/* open the modal if the customer hasn't seen it yet **/
				setOpen(
					!apiResponse.data?.getClientUserWhere?.user?.seenOnboardingModalAt
				);
			}
		};
		// fetch if signed in, and we haven't already run this, so we don't immediately hide the modal after just showing it
		if ( clientUser && typeof open === 'undefined' ) {
			fetchAndUpdate();
		}
	}, [
		apiResponse,
		clientUser,
		router.pathname,
		open,
		showError
	] );

	useEffect( () => {
		if ( open && clientUser ) {
			updateSelf( {
				variables: {
					data: { seenOnboardingModalAt: new Date() },
				},
			} );
		}
	}, [
		open,
		clientUser,
		updateSelf
	] );

	const onClose = useCallback( () => setOpen( false ), [] );

	return {
		open: !!open,
		isClientUser: !!clientUser,
		onClose,
		setOpen,
	};
};
